/**
 * The 5S target: the score a machine has to reach to count as in order.
 *
 * Eighty was written into every 5S screen — the board's chips and sparkline,
 * the walk's running total, the detail sheet and its "Šta prvo popraviti"
 * list, the Početna feed and the Analitika tiles, bars and table — each with
 * its own `>= 80` and `>= 60`. A factory that holds itself to ninety saw a
 * green board it disagreed with, and one just starting 5S saw nothing but red.
 * The target is now a company setting, chosen by the glavni administrator, and
 * this is the one place the browser reads it.
 *
 * Shaped like `lib/timezone.ts` and `lib/currency.ts`, for their reasons: the
 * value is a property of the company, so it is not stored and the only correct
 * one is whatever `/auth/me` last said; and it is readable without a hook, so
 * the rules below can be tested without a DOM. Until a session exists, and for
 * a server that predates the setting, the target is eighty — what every screen
 * drew before, so nothing changes for a company that never chose.
 *
 * ## The bands
 *
 * Three, as before: on target, short of it, and far below it. The middle band
 * is twenty points wide and moves with the target, so the default is exactly
 * the old 80 / 60 split. A fixed sixty would have put a 94 and a 61 in the
 * same band for a company aiming at 95. `App\Models\Company::fiveSTone()` on
 * the server applies the same rule to the printed sheet.
 */

/** What a company that never chose gets, matching `Company::FIVE_S_TARGET`. */
export const DEFAULT_FIVE_S_TARGET = 80

/** The range the setting takes — the server refuses anything outside it. */
export const FIVE_S_TARGET_MIN = 50
export const FIVE_S_TARGET_MAX = 100

/** How far under the target a score is still only short of it, in points. */
export const FIVE_S_WARN_BAND = 20

let activeTarget = DEFAULT_FIVE_S_TARGET

/** A whole number of percent inside the range the setting takes. */
export function isValidFiveSTarget(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= FIVE_S_TARGET_MIN &&
    value <= FIVE_S_TARGET_MAX
  )
}

/**
 * Take the target from a signed-in session.
 *
 * Called wherever the user is set, sign-out included: passing nothing returns
 * to the default, so the shared terminal's next sign-in does not keep the last
 * person's company. Anything that is not a target the server could have sent
 * falls back the same way, rather than colouring every machine green.
 */
export function adoptCompanyFiveSTarget(target: number | null | undefined): void {
  activeTarget = isValidFiveSTarget(target) ? target : DEFAULT_FIVE_S_TARGET
}

/** The score every 5S screen counts as on target, in percent. */
export function fiveSTarget(): number {
  return activeTarget
}

export type FiveSTone = 'good' | 'warn' | 'bad'

/**
 * The band a percentage falls in.
 *
 * The target is a parameter for the one screen that has to draw a target
 * nobody has saved yet — the setting's own preview. Everywhere else it is
 * left to default to the company's.
 */
export function fiveSTone(percentage: number, target: number = fiveSTarget()): FiveSTone {
  if (percentage >= target) return 'good'
  if (percentage >= target - FIVE_S_WARN_BAND) return 'warn'

  return 'bad'
}

const BADGE_COLOR: Record<FiveSTone, 'emerald' | 'amber' | 'rose'> = {
  good: 'emerald',
  warn: 'amber',
  bad: 'rose',
}

/** The badge colour for a percentage — always shown beside the number itself. */
export function scoreColor(
  percentage: number,
  target: number = fiveSTarget(),
): 'emerald' | 'amber' | 'rose' {
  return BADGE_COLOR[fiveSTone(percentage, target)]
}

/**
 * Where each band starts, for saying them in words.
 *
 * `warnTo` is the last whole percent short of the target, which is how a
 * person reads "60–79 %"; a 79.5 is still amber, and the colour beside it says
 * so where the label rounds.
 */
export function fiveSBands(target: number = fiveSTarget()): {
  target: number
  warnFrom: number
  warnTo: number
} {
  return { target, warnFrom: target - FIVE_S_WARN_BAND, warnTo: target - 1 }
}
